import React, { useState, useEffect } from 'react';

// Datos de demostración
const initialUsers = [
  {
    id: 1,
    name: "Ana García",
    email: "ana@empresa.com",
    role: "admin",
    status: "active"
  },
  {
    id: 2,
    name: "Carlos López",
    email: "carlos@empresa.com",
    role: "editor",
    status: "active"
  },
  {
    id: 3,
    name: "María Rodríguez",
    email: "maria@empresa.com",
    role: "viewer",
    status: "inactive"
  },
  {
    id: 4,
    name: "Juan Pérez",
    email: "juan@empresa.com",
    role: "editor",
    status: "active"
  }
];

const rolesConfig = {
  admin: {
    name: "Administrador",
    permissions: ["create", "read", "update", "delete", "manage_users"]
  },
  editor: {
    name: "Editor",
    permissions: ["create", "read", "update"]
  },
  viewer: {
    name: "Visualizador",
    permissions: ["read"]
  }
};

const UserRoleControlSystem = () => {
  // Estados del sistema
  const [users, setUsers] = useState([]);
  const [currentPanel, setCurrentPanel] = useState('users');
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // Método específico para cargar información
  const loadData = () => {
    setLoading(true);
    // Simulamos una carga asíncrona de datos
    setTimeout(() => {
      setUsers(initialUsers);
      setLoading(false);
      showNotification('Datos cargados exitosamente', 'success');
    }, 1000);
  };

  // Método específico para mostrar notificaciones
  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  // Cargar datos al inicializar el componente
  useEffect(() => {
    loadData();
  }, []);

  // Método para cambiar entre paneles
  const changePanel = (panelName) => {
    setCurrentPanel(panelName);
  };

  // Método para seleccionar un usuario
  const selectUser = (user) => {
    setSelectedUser(user);
    changePanel('userDetail');
  };

  // Método para actualizar el rol de un usuario
  const updateUserRole = (userId, newRole) => {
    setUsers(users.map(user => 
      user.id === userId ? { ...user, role: newRole } : user
    ));
    showNotification(`Rol actualizado correctamente`, 'success');
  };

  // Método para cambiar el estado de un usuario
  const toggleUserStatus = (userId) => {
    setUsers(users.map(user => 
      user.id === userId 
        ? { ...user, status: user.status === 'active' ? 'inactive' : 'active' } 
        : user
    ));
    showNotification(`Estado de usuario actualizado`, 'info');
  };

  // Renderizar panel de lista de usuarios
  const renderUsersPanel = () => (
    <div className="panel">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Gestión de Usuarios</h2>
        <button className="btn btn-primary" onClick={loadData}>
          <i className="bi bi-arrow-clockwise me-2"></i>Recargar Datos
        </button>
      </div>
      
      {loading ? (
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-2">Cargando usuarios...</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead className="table-dark">
              <tr>
                <th>Nombre</th>
                <th>Email</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`badge bg-${user.role === 'admin' ? 'danger' : user.role === 'editor' ? 'warning' : 'info'}`}>
                      {rolesConfig[user.role].name}
                    </span>
                  </td>
                  <td>
                    <span className={`badge bg-${user.status === 'active' ? 'success' : 'secondary'}`}>
                      {user.status === 'active' ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <button 
                      className="btn btn-sm btn-outline-primary me-1"
                      onClick={() => selectUser(user)}
                    >
                      <i className="bi bi-eye"></i> Ver
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => toggleUserStatus(user.id)}
                    >
                      <i className={`bi bi-${user.status === 'active' ? 'x-circle' : 'check-circle'}`}></i> 
                      {user.status === 'active' ? ' Desactivar' : ' Activar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Renderizar panel de detalle de usuario
  const renderUserDetailPanel = () => {
    if (!selectedUser) return null;
    
    return (
      <div className="panel">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Detalle de Usuario</h2>
          <button className="btn btn-secondary" onClick={() => changePanel('users')}>
            <i className="bi bi-arrow-left me-2"></i>Volver a la lista
          </button>
        </div>
        
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">{selectedUser.name}</h5>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-6">
                <strong>Email:</strong> {selectedUser.email}
              </div>
              <div className="col-md-6">
                <strong>Estado:</strong> 
                <span className={`badge bg-${selectedUser.status === 'active' ? 'success' : 'secondary'} ms-2`}>
                  {selectedUser.status === 'active' ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>
            
            <div className="row mb-4">
              <div className="col-12">
                <strong>Rol actual:</strong> 
                <span className={`badge bg-${selectedUser.role === 'admin' ? 'danger' : selectedUser.role === 'editor' ? 'warning' : 'info'} ms-2`}>
                  {rolesConfig[selectedUser.role].name}
                </span>
              </div>
            </div>
            
            <h5>Cambiar Rol</h5>
            <div className="d-flex flex-wrap gap-2">
              {Object.keys(rolesConfig).map(roleKey => (
                <button
                  key={roleKey}
                  className={`btn ${selectedUser.role === roleKey ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => updateUserRole(selectedUser.id, roleKey)}
                  disabled={selectedUser.role === roleKey}
                >
                  {rolesConfig[roleKey].name}
                </button>
              ))}
            </div>
            
            <hr className="my-4" />
            
            <h5>Permisos del Rol</h5>
            <div className="d-flex flex-wrap gap-2">
              {rolesConfig[selectedUser.role].permissions.map(permission => (
                <span key={permission} className="badge bg-success">
                  {permission}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Renderizar panel de información de roles
  const renderRolesPanel = () => (
    <div className="panel">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Gestión de Roles y Permisos</h2>
        <button className="btn btn-secondary" onClick={() => changePanel('users')}>
          <i className="bi bi-arrow-left me-2"></i>Volver a usuarios
        </button>
      </div>
      
      <div className="row">
        {Object.keys(rolesConfig).map(roleKey => (
          <div key={roleKey} className="col-md-4 mb-4">
            <div className="card h-100">
              <div className={`card-header bg-${roleKey === 'admin' ? 'danger' : roleKey === 'editor' ? 'warning' : 'info'} text-white`}>
                <h5 className="card-title mb-0">{rolesConfig[roleKey].name}</h5>
              </div>
              <div className="card-body">
                <h6 className="card-subtitle mb-3">Permisos:</h6>
                <div className="d-flex flex-wrap gap-1">
                  {rolesConfig[roleKey].permissions.map(permission => (
                    <span key={permission} className="badge bg-secondary">
                      {permission}
                    </span>
                  ))}
                </div>
              </div>
              <div className="card-footer">
                <small className="text-muted">
                  {roleKey === 'admin' 
                    ? 'Acceso completo al sistema' 
                    : roleKey === 'editor' 
                      ? 'Puede crear y editar contenido' 
                      : 'Solo visualización de información'}
                </small>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="container-fluid py-4">
      {/* Notificación */}
      {notification.show && (
        <div className={`alert alert-${notification.type} alert-dismissible fade show`} role="alert">
          {notification.message}
          <button type="button" className="btn-close" onClick={() => setNotification({ show: false, message: '', type: '' })}></button>
        </div>
      )}
      
      {/* Encabezado */}

      
      {/* Navegación */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark rounded mb-4">
        <div className="container-fluid">
          <button 
            className="navbar-toggler" 
            type="button" 
            data-bs-toggle="collapse" 
            data-bs-target="#navbarNav"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav">
              <li className="nav-item">
                <button 
                  className={`nav-link btn btn-link ${currentPanel === 'users' ? 'active' : ''}`}
                  onClick={() => changePanel('users')}
                >
                  <i className="bi bi-people-fill me-1"></i> Usuarios
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link btn btn-link ${currentPanel === 'roles' ? 'active' : ''}`}
                  onClick={() => changePanel('roles')}
                >
                  <i className="bi bi-shield-lock me-1"></i> Roles y Permisos
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>
      
      {/* Contenido principal */}
      <main>
        {currentPanel === 'users' && renderUsersPanel()}
        {currentPanel === 'userDetail' && renderUserDetailPanel()}
        {currentPanel === 'roles' && renderRolesPanel()}
      </main>
      
      {/* Pie de página */}
      <footer className="mt-5 pt-4 border-top">
        <p className="text-center text-muted">© 2023 Sistema de Gestión de Usuarios - Demo</p>
      </footer>
    </div>
  );
};

export default UserRoleControlSystem;